import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import clsx from 'clsx'

import { useZodForm, SubmitButton, Form } from '@/components/Forms'
import { useGetEmployees } from '@/modules/Auth/api/useGetEmployees'
import { Button } from '@/ui/Button'
import { Log } from '@/types/logs'
import { OfficialReportRequestBody, OfficialReportStatus, OfficialReportType } from '@/types/officialReport'
import { useModal } from '@/components/Modal'
import { useGetCodeName } from '@/modules/CombinedLogs/hooks/useGetCodeName'
import { useSendOfficialReport } from '../api/useSendOfficialReport'
import { useDeleteOfficialReport } from '../api/useDeleteOfficialReport'
import { commonSchema, FormValuesType, headwaySchema, lateSchema, mileageLossSchema } from '../schema'
import styles from './OfficialReportModal.module.scss'
import confirmStyles from '@/components/Modal/ui/DeleteModal.module.scss'

import {
    isHeadwayOfficialReportType,
    isLateOfficialReportType,
    isGenericOfficialReportType,
} from '../helpers/checkReportType'
import { format, isToday } from 'date-fns'

import { CommonInformation } from './components/CommonInformation'
import { DriverAndVehicle } from './components/DriverAndVehicle'
import { IncidentDetails } from './components/IncidentDetails'
import { DescriptionPreview } from './components/DescriptionPreview'

import { validateDriverEmployeeNumber } from '../validation/validateDriverEmployeeNumber'
import { validateHeadways } from '../validation/validateHeadways'
import { generateOfficialReportDefaultValues } from '../helpers/generateOfficialReportDefaultValues'
import { DRAFT_ERROR_FIELDS, OFFICIAL_REPORT_FORM_FIELDS, OFFICIAL_REPORT_TYPE_MAP } from '../constant'
import { validateTakeoverTime } from '../validation/validateTakeoverTime'

interface Props {
    log: Log
    type: OfficialReportType
    submitCallback?: (data: OfficialReportRequestBody) => void
    deleteCallback?: () => void
    closeRequested?: boolean
    onCloseHandled: () => void
}

export const OfficialReportForm = ({
    log,
    type,
    submitCallback,
    deleteCallback,
    closeRequested,
    onCloseHandled,
}: Props) => {
    const { data: employees = [] } = useGetEmployees()
    const getCodeName = useGetCodeName()
    const { closeModal } = useModal()
    const [lookupWarning, setLookupWarning] = useState<string | undefined>(undefined)
    const [showConfirmation, setShowConfirmation] = useState(false)
    const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false)
    const containerRef = useRef<HTMLDivElement>(null)

    // When an inline confirmation overlay is dismissed by clicking its Cancel button,
    // focus is lost (the button is removed from DOM). Refocus the parent dialog so
    // its ESC keydown listener continues to work.
    const refocusDialog = useCallback(() => {
        requestAnimationFrame(() => containerRef.current?.closest('dialog')?.focus())
    }, [])

    const dismissDeleteConfirmation = useCallback(() => {
        setShowDeleteConfirmation(false)
        refocusDialog()
    }, [refocusDialog])

    const dismissUnsavedConfirmation = useCallback(() => {
        setShowConfirmation(false)
        refocusDialog()
    }, [refocusDialog])

    const officialReportSchema = useMemo(() => {
        let schema = commonSchema

        if (isGenericOfficialReportType(type)) {
            schema = schema.merge(mileageLossSchema)
        }

        if (isLateOfficialReportType(type)) {
            schema = schema.merge(lateSchema)
        }

        if (isHeadwayOfficialReportType(type)) {
            schema = schema.merge(headwaySchema)
        }

        return schema.superRefine((_data, ctx) => {
            const data = _data as FormValuesType
            setLookupWarning(validateDriverEmployeeNumber({ data, ctx, employees }))

            if (isLateOfficialReportType(type)) {
                validateTakeoverTime({ data, ctx })
            }

            if (isHeadwayOfficialReportType(type)) {
                validateHeadways({ data, ctx })
            }
        })
    }, [employees])

    const officialReportDefaultValues = generateOfficialReportDefaultValues(log, type)
    const isReportSubmitted = log.report?.status === OfficialReportStatus.Submitted

    const form = useZodForm({
        schema: officialReportSchema,
        mode: 'onChange',
        criteriaMode: 'all',
        defaultValues: officialReportDefaultValues,
    })

    const { isDirty } = form.formState

    // Handle close requests from the parent modal (backdrop click, X, Escape)
    useEffect(() => {
        if (!closeRequested) return
        onCloseHandled()

        if (showConfirmation) {
            setShowConfirmation(false)
        } else if (showDeleteConfirmation) {
            setShowDeleteConfirmation(false)
        } else if (isDirty) {
            setShowConfirmation(true)
        } else {
            closeModal()
        }
    }, [closeRequested]) // eslint-disable-line react-hooks/exhaustive-deps

    const getReportType = () =>
        type in OFFICIAL_REPORT_TYPE_MAP ? OFFICIAL_REPORT_TYPE_MAP[type] : getCodeName(log.code)

    const { handleSendOfficialReport, isPending } = useSendOfficialReport({ log, type, callback: submitCallback })
    const { performDelete } = useDeleteOfficialReport({
        log,
        callback: deleteCallback,
    })

    const handleSubmitForm = (data: FormValuesType) => {
        handleSendOfficialReport(OfficialReportStatus.Submitted, data)
    }

    const handleSaveDraft = () => {
        const values = form.getValues()

        handleSendOfficialReport(OfficialReportStatus.Draft, values)
    }

    const handleDelete = () => {
        setShowDeleteConfirmation(true)
    }

    const handleConfirmDelete = () => {
        performDelete()
        closeModal()
    }

    const handleCancel = () => {
        if (isDirty) {
            setShowConfirmation(true)
        } else {
            closeModal()
        }
    }

    const isSubmitButtonDisabled = !form.formState.isValid || isPending
    const isSaveDraftDisabled = useMemo(
        () => isPending || DRAFT_ERROR_FIELDS.some((field) => field in form.formState.errors),
        [form.formState]
    )

    return (
        <div ref={containerRef} className={styles.formContainer}>
            <Form form={form} className={styles.form} onSubmit={handleSubmitForm}>
                <div
                    className={clsx(styles.content, { [styles.noScroll]: showDeleteConfirmation || showConfirmation })}
                >
                    <div>
                        <h1 className={styles.title}>Official report - {getReportType()}</h1>
                        {log.codeTypeReasonInfo && isGenericOfficialReportType(type) && (
                            <p className={styles.description}>{log.codeTypeReasonInfo.reasonDescription}</p>
                        )}
                    </div>
                    <CommonInformation reportType={type} />
                    <DriverAndVehicle log={log} warningText={lookupWarning} />
                    <IncidentDetails type={type} />
                    {!isGenericOfficialReportType(type) && <DescriptionPreview log={log} type={type} />}
                </div>
                <div
                    className={clsx(styles.footer, {
                        [styles.withDelete]: log.report && !log.codeTypeReasonInfo?.isOfficialReportRequired,
                    })}
                >
                    {log.report && !log.codeTypeReasonInfo?.isOfficialReportRequired && (
                        <Button variant="transparent" onClick={handleDelete} type="button">
                            Delete report
                        </Button>
                    )}
                    <div className={styles.actionGroup}>
                        {!isReportSubmitted && (
                            <Button
                                variant="secondary"
                                rounded
                                disabled={isSaveDraftDisabled}
                                onClick={handleSaveDraft}
                            >
                                Save draft
                            </Button>
                        )}
                        {isReportSubmitted && (
                            <Button variant="secondary" rounded onClick={handleCancel}>
                                Cancel
                            </Button>
                        )}
                        <SubmitButton rounded disabled={isSubmitButtonDisabled}>
                            {isReportSubmitted ? 'Update report' : 'Submit report'}
                        </SubmitButton>
                    </div>
                </div>
            </Form>
            {showDeleteConfirmation &&
                (() => {
                    const values = form.getValues()
                    const incidentDate = values[OFFICIAL_REPORT_FORM_FIELDS.dateOfIssue]
                    const deleteFrom = isToday(incidentDate) ? 'today' : format(incidentDate, 'd/MM/y')
                    const reportLabel = getReportType()

                    return (
                        <div className={styles.confirmationOverlay} onClick={dismissDeleteConfirmation}>
                            <div className={styles.confirmationContent} onClick={(e) => e.stopPropagation()}>
                                <div className={confirmStyles.information}>
                                    <h1 className={confirmStyles.title}>Delete report?</h1>
                                    <p className={confirmStyles.description}>
                                        This will delete <strong>Official report - {reportLabel}</strong> from{' '}
                                        <strong>{deleteFrom}</strong>. This cannot be undone.
                                    </p>
                                </div>
                                <div className={confirmStyles.footer}>
                                    <Button rounded variant="tertiary" onClick={dismissDeleteConfirmation}>
                                        Cancel
                                    </Button>
                                    <Button rounded variant="secondary" onClick={handleConfirmDelete}>
                                        Delete
                                    </Button>
                                </div>
                            </div>
                        </div>
                    )
                })()}
            {showConfirmation && (
                <div className={styles.confirmationOverlay} onClick={dismissUnsavedConfirmation}>
                    <div className={styles.confirmationContent} onClick={(e) => e.stopPropagation()}>
                        <div className={confirmStyles.information}>
                            <h1 className={confirmStyles.title}>Unsaved changes</h1>
                            <p className={confirmStyles.description}>
                                Are you sure you want to discard your unsaved changes?
                            </p>
                        </div>
                        <div className={confirmStyles.footer}>
                            <Button rounded variant="tertiary" onClick={dismissUnsavedConfirmation}>
                                Keep editing
                            </Button>
                            <Button rounded variant="secondary" onClick={closeModal}>
                                Discard
                            </Button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}
